import { StandardEvents } from '@shopify/events';
import { setupViewer } from '@theme/kh-viewer';

const CART_DELAY_MS = 400; // pdp-column §3: «✓ Додано» stays 400ms, then the cart panel
const ERROR_MS = 4000;
const FINISH_MS = 120;
const mobileQuery = window.matchMedia('(max-width: 989px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
/** @param {Element} el @param {string} name */
const restartAnimation = (el, name) => {
  el.classList.remove(name);
  void (/** @type {HTMLElement} */ (el).offsetWidth);
  el.classList.add(name);
};

/**
 * Product page on top of Horizon's variant-picker / product-price / product-form-component.
 * Horizon updates the picker, price and add-to-cart button on variant change; this element
 * refreshes our own regions ([data-kh-live]) from the same response and drives the
 * «Додаємо… → ✓ Додано → кошик» sequence, the mobile sticky button, the gallery dots and the full-screen photo viewer.
 */
class KhProduct extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();
  /** @type {IntersectionObserver | null} */
  #ctaObserver = null;
  /** @type {IntersectionObserver | null} */
  #galleryObserver = null;

  connectedCallback() {
    const { signal } = this.#abort;
    const section = this.closest('.shopify-section') || this;

    section.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal, capture: true });
    this.addEventListener('click', this.#onClick, { signal });
    // A size chip: «Розмір: L» and the button label follow at once (Horizon re-renders them with the same text later)
    this.addEventListener(
      'click',
      (event) => {
        const input = /** @type {HTMLInputElement | null} */ (
          /** @type {HTMLElement} */ (event.target).closest?.('.kh-pdp__sizes input')
        );
        if (!input) return;
        this.#rollValue('[data-kh-size-value]', input.value, false);
        if (input.dataset.optionAvailable === 'true') this.#showSize(input.value);
      },
      { signal }
    );
    // «Колір: Чорний»: the new colour name comes up from below
    this.addEventListener(
      'change',
      (event) => {
        const input = /** @type {HTMLInputElement} */ (event.target);
        if (input.closest?.('.kh-pdp__swatches')) this.#rollValue('[data-kh-color-value]', input.value, false);
      },
      { signal }
    );
    // The cart panel closed: the button is Ink «Додати в кошик · L» again
    document.addEventListener(
      'theme-drawer:close',
      (event) => {
        if (/** @type {HTMLElement} */ (event.target)?.id !== 'cart-drawer') return;
        const button = this.#addButton;
        if (button?.classList.contains('is-success')) this.#setButtonState(button, 'idle');
      },
      { signal }
    );
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    this.#observeCta();
    this.#observeGallery();
    this.#setupDelivery(signal);
    setupViewer(this, signal);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#ctaObserver?.disconnect();
    this.#galleryObserver?.disconnect();
  }

  /* ---------- Variant change ---------- */

  /** @param {Event & { promise?: Promise<{ detail?: { html?: Document } }> }} event */
  #onProductSelect = (event) => {
    if (/** @type {Element} */ (event.target).closest?.('product-card, quick-add-dialog')) return;
    event.promise
      ?.then(({ detail } = {}) => {
        if (detail?.html) this.#applyHtml(detail.html);
      })
      .catch(() => {});
  };

  /** @param {Document} html */
  #applyHtml(html) {
    this.querySelectorAll('[data-kh-live]').forEach((region) => {
      const key = region.getAttribute('data-kh-live');
      const fresh = html.querySelector(`[data-kh-live="${key}"]`);
      if (!fresh) return;
      const wasOpen = region.querySelector('dialog[open]');
      if (wasOpen) return; // never swap an open dialog under the user
      if (key === 'pickup') {
        this.#updateDelivery(region, fresh);
        return;
      }
      if (key === 'status') {
        // Only the words and the dot change; the line keeps its height (the button never jumps)
        const line = region.querySelector('.kh-pdp__stock');
        const next = fresh.querySelector('.kh-pdp__stock');
        if (line && next && (line.textContent !== next.textContent || line.className !== next.className)) {
          next.classList.add('is-swapping');
          line.replaceWith(next);
        }
        return;
      }
      region.innerHTML = fresh.innerHTML;
    });

    const cta = this.querySelector('[data-kh-cta]');
    const freshCta = html.querySelector('[data-kh-cta]');
    if (cta && freshCta) cta.setAttribute('data-available', freshCta.getAttribute('data-available') || 'true');

    const sticky = this.querySelector('[data-kh-sticky]');
    const freshSticky = html.querySelector('[data-kh-sticky]');
    if (sticky && freshSticky) {
      const label = freshSticky.querySelector('[data-kh-sticky-label]');
      const price = freshSticky.querySelector('[data-kh-sticky-price]');
      const notifyLabel = html.querySelector('[data-kh-live="notify"] .kh-btn__label');
      const available = freshCta?.getAttribute('data-available') !== 'false';
      const stickyLabel = sticky.querySelector('[data-kh-sticky-label]');
      const stickyPrice = /** @type {HTMLElement | null} */ (sticky.querySelector('[data-kh-sticky-price]'));
      if (stickyLabel) stickyLabel.textContent = (available ? label?.textContent : notifyLabel?.textContent) || '';
      if (stickyPrice) {
        stickyPrice.innerHTML = price?.innerHTML || '';
        stickyPrice.hidden = !available;
      }
    }
  }

  /* ---------- Add to cart states ---------- */

  get #addButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.kh-pdp__add'));
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = /** @type {HTMLElement} */ (event.target);
    if (!form.closest('product-form-component')) return;
    const button = this.#addButton;
    if (!button || button.disabled) return;
    this.#setButtonState(button, 'loading');
  };

  /** @type {number | undefined} */
  #safetyTimer;
  /** @type {number | undefined} */
  #errorTimer;

  /**
   * Horizon dispatches the cart event from product-form-component, sometimes before our submit
   * listener runs, so react to any add that comes from this product regardless of order.
   * A refused add (sold out meanwhile…) still resolves, with detail.didError.
   * @param {Event & { promise?: Promise<{ detail?: { didError?: boolean } }> }} event
   */
  #onCartUpdate = (event) => {
    const button = this.#addButton;
    const source = /** @type {Node} */ (event.target);
    const fromHere = source instanceof Node && this.contains(source);
    if (!button || (!fromHere && !button.classList.contains('is-loading'))) return;

    this.#setButtonState(button, 'loading');
    window.clearTimeout(this.#safetyTimer);
    this.#safetyTimer = window.setTimeout(() => settle(false), 10000);

    /** @param {boolean} ok */
    const settle = async (ok) => {
      window.clearTimeout(this.#safetyTimer);
      if (!button.classList.contains('is-loading')) return;
      // The Steel fill runs out, then the answer shows
      button.classList.add('is-finishing');
      await wait(reducedMotion.matches ? 0 : FINISH_MS);
      button.classList.remove('is-finishing');
      if (!ok) {
        this.#setButtonState(button, 'idle');
        this.#showError();
        return;
      }
      this.#setButtonState(button, 'success');
      await wait(reducedMotion.matches ? 0 : CART_DELAY_MS);
      if (!button.classList.contains('is-success')) return;
      const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.getElementById('cart-drawer'));
      if (drawer?.open) drawer.open();
      else this.#setButtonState(button, 'idle');
    };
    if (event.promise) {
      event.promise.then((result) => settle(!result?.detail?.didError)).catch(() => settle(false));
    } else {
      settle(true);
    }
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {'idle' | 'loading' | 'success'} state
   */
  #setButtonState(button, state) {
    const label = /** @type {HTMLElement | null} */ (button.querySelector('[data-kh-add-label]'));
    if (label && state !== 'idle' && button.dataset.labelIdle === undefined) {
      button.dataset.labelIdle = label.innerHTML;
      button.dataset.labelSize = label.querySelector('[data-kh-roll]')?.textContent || '';
    }
    const size = button.dataset.labelSize || '';
    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-success', state === 'success');
    button.setAttribute('aria-disabled', String(state !== 'idle'));
    if (!label) return;
    if (state === 'loading') label.textContent = button.dataset.labelAdding || '';
    if (state === 'success') {
      label.textContent = button.dataset.labelAdded || '';
      if (size) label.append(' · ', size);
    }
    if (state === 'idle' && button.dataset.labelIdle !== undefined) {
      label.innerHTML = button.dataset.labelIdle;
      delete button.dataset.labelIdle;
      delete button.dataset.labelSize;
    }
  }

  /** Sand row under the button for 4 s */
  #showError() {
    const row = this.querySelector('[data-kh-add-error]');
    if (!row) return;
    window.clearTimeout(this.#errorTimer);
    row.classList.add('is-open');
    this.#errorTimer = window.setTimeout(() => row.classList.remove('is-open'), ERROR_MS);
  }

  /**
   * The label follows a size click at once: in «Додати в кошик · L» only the letter moves.
   * @param {string} size
   */
  #showSize(size) {
    const sticky = this.querySelector('[data-kh-sticky-label]');
    const add = this.#addButton?.dataset.labelAdd;
    if (sticky && add) sticky.textContent = `${add} · ${size}`;
    const button = this.#addButton;
    const roll = button?.querySelector('[data-kh-add-label] [data-kh-roll]');
    if (!button || !roll || button.classList.contains('is-loading') || button.classList.contains('is-success')) return;
    if (roll.textContent === size) return;
    roll.textContent = size;
    if (!reducedMotion.matches) restartAnimation(roll, 'is-in');
  }

  /**
   * Replaces a value in a label and lets only it come up 4px (also when it first appears).
   * @param {string} selector
   * @param {string} value
   * @param {boolean} force
   */
  #rollValue(selector, value, force) {
    const el = this.querySelector(selector);
    if (!el || (!force && el.textContent === value)) return;
    el.textContent = value;
    if (!reducedMotion.matches) restartAnimation(el, 'is-in');
  }

  /* ---------- Delivery card (pdp-column §4) ---------- */

  /** @type {number | undefined} */
  #hubsTimer;

  /** @param {AbortSignal} signal */
  #setupDelivery(signal) {
    this.#applyNpDay(this);
    this.addEventListener(
      'click',
      (event) => {
        // The hub list and the accordions (pdp-column §4, §5) open on their own, independently
        const toggle = /** @type {HTMLButtonElement | null} */ (
          /** @type {HTMLElement} */ (event.target).closest?.('[data-kh-hubs-toggle], [data-kh-fold-toggle]')
        );
        if (!toggle || toggle.disabled) return;
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        // Accordions: one open at a time — opening one folds the other (the hub list is on its own)
        if (open && toggle.hasAttribute('data-kh-fold-toggle')) {
          this.querySelectorAll('[data-kh-fold-toggle][aria-expanded="true"]').forEach((other) => {
            if (other !== toggle) this.#setFold(/** @type {HTMLElement} */ (other), false);
          });
        }
        this.#setFold(toggle, open);
      },
      { signal }
    );
    // A size / colour was picked: if the hubs answer takes longer than 300ms, say «перевіряємо…» (no spinner)
    this.addEventListener(
      'change',
      (event) => {
        if (!(/** @type {HTMLElement} */ (event.target).closest?.('.kh-pdp__picker'))) return;
        window.clearTimeout(this.#hubsTimer);
        this.#hubsTimer = window.setTimeout(() => this.#showChecking(), 300);
      },
      { signal }
    );
  }

  /**
   * @param {HTMLElement} toggle
   * @param {boolean} open
   */
  #setFold(toggle, open) {
    const fold = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!fold) return;
    toggle.setAttribute('aria-expanded', String(open));
    fold.classList.toggle('is-open', open);
    if (!open) return;
    // The page does not jump: scroll only if the opened block ends below the screen, after the transition
    window.setTimeout(() => {
      if (fold.getBoundingClientRect().bottom > window.innerHeight) {
        fold.scrollIntoView({ block: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }, reducedMotion.matches ? 0 : 260);
  }

  /**
   * Нова Пошта «Сьогодні / Завтра» by Kyiv time against the cutoff hour (the page may come from a cache).
   * @param {ParentNode} root
   */
  #applyNpDay(root) {
    const tag = /** @type {HTMLElement | null} */ (root.querySelector('[data-kh-np-day]'));
    const card = /** @type {HTMLElement | null} */ (root.querySelector('.kh-dlv'));
    if (!tag || !card) return;
    const cutoff = Number(card.dataset.cutoff) || 16;
    const hour = Number(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Kyiv', hour: 'numeric', hourCycle: 'h23' }).format(new Date())
    );
    const late = hour >= cutoff;
    tag.classList.toggle('kh-tag--no', late);
    tag.textContent = (late ? tag.dataset.tomorrow : tag.dataset.today) || tag.textContent;
  }

  #showChecking() {
    const answer = /** @type {HTMLElement | null} */ (this.querySelector('[data-kh-hubs-ans]'));
    const card = /** @type {HTMLElement | null} */ (this.querySelector('.kh-dlv'));
    if (!answer || !card) return;
    const previous = { key: answer.dataset.key, html: answer.innerHTML };
    answer.dataset.key = 'wait';
    this.#swap(answer, `<span class="kh-dlv__hint">${card.dataset.checking || ''}</span>`);
    // The variant request failed (offline…): no update will come, so put the last answer back
    this.#hubsTimer = window.setTimeout(() => {
      if (answer.dataset.key !== 'wait') return;
      if (previous.key) answer.dataset.key = previous.key;
      else delete answer.dataset.key;
      this.#swap(answer, previous.html);
    }, 8000);
  }

  /**
   * Updates the card from a fresh render in place: what changed is what moves. Same «yes» answer → only
   * «3 Хабах» rolls; a different answer fades out / in; hub statuses crossfade; the list keeps its height
   * and stays open unless there are no hubs any more.
   * @param {Element} card
   * @param {Element} fresh
   */
  #updateDelivery(card, fresh) {
    window.clearTimeout(this.#hubsTimer);
    const live = /** @type {HTMLElement} */ (card);
    const next = /** @type {HTMLElement} */ (fresh);
    this.#applyNpDay(next.parentElement || next);
    live.dataset.hubs = next.dataset.hubs || '0';

    const np = /** @type {HTMLElement | null} */ (live.querySelector('[data-kh-np]'));
    const npFresh = next.querySelector('[data-kh-np]');
    if (np && npFresh) this.#swap(np, npFresh.innerHTML);

    const answer = /** @type {HTMLElement | null} */ (live.querySelector('[data-kh-hubs-ans]'));
    const answerFresh = /** @type {HTMLElement | null} */ (next.querySelector('[data-kh-hubs-ans]'));
    if (answer && answerFresh) {
      const roll = answer.querySelector('[data-kh-roll]');
      const rollFresh = answerFresh.querySelector('[data-kh-roll]');
      if (answer.dataset.key === 'ok' && answerFresh.dataset.key === 'ok' && roll && rollFresh) {
        if (roll.textContent !== rollFresh.textContent) {
          roll.textContent = rollFresh.textContent;
          restartAnimation(roll, 'is-in');
        }
      } else {
        this.#swap(answer, answerFresh.innerHTML);
      }
      answer.dataset.key = answerFresh.dataset.key || '';
    }

    const count = Number(live.dataset.hubs) || 0;
    const toggle = /** @type {HTMLButtonElement | null} */ (live.querySelector('[data-kh-hubs-toggle]'));
    if (toggle) {
      toggle.disabled = count === 0;
      live.classList.toggle('is-flat', count === 0);
      toggle.querySelector('.kh-pm')?.classList.toggle('is-hidden', count === 0);
      if (count === 0 && toggle.getAttribute('aria-expanded') === 'true') this.#setFold(toggle, false);
    }

    live.querySelectorAll('[data-kh-hub]').forEach((hub) => {
      const hubFresh = next.querySelector(`[data-kh-hub="${hub.getAttribute('data-kh-hub')}"]`);
      if (!hubFresh) return;
      hub.classList.toggle('is-off', hubFresh.classList.contains('is-off'));
      const text = hub.querySelector('.kh-hub__status span');
      const textFresh = hubFresh.querySelector('.kh-hub__status span');
      if (text && textFresh && text.textContent !== textFresh.textContent) {
        text.textContent = textFresh.textContent;
        restartAnimation(text, 'is-in');
      }
    });
  }

  /**
   * Old content fades out in 120ms, the new one comes in (CSS .is-in)
   * @param {HTMLElement} el
   * @param {string} html
   */
  async #swap(el, html) {
    if (el.innerHTML.trim() === html.trim()) return;
    const turn = (Number(el.dataset.turn) || 0) + 1;
    el.dataset.turn = String(turn);
    if (!reducedMotion.matches && el.innerHTML.trim()) {
      el.classList.add('is-out');
      await wait(120);
      if (el.dataset.turn !== String(turn)) return;
    }
    el.innerHTML = html;
    el.classList.remove('is-out');
    restartAnimation(el, 'is-in');
  }

  /* ---------- Mobile sticky button, gallery dots ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(/** @type {HTMLElement} */ (event.target).closest('[data-kh-sticky-btn]'))) return;
    const available = this.querySelector('[data-kh-cta]')?.getAttribute('data-available') !== 'false';
    const target = available ? this.#addButton : this.querySelector('[data-kh-live="notify"] a');
    /** @type {HTMLElement | null} */ (target)?.click();
  };

  #observeCta() {
    const cta = this.querySelector('[data-kh-cta]');
    const sticky = /** @type {HTMLElement | null} */ (this.querySelector('[data-kh-sticky]'));
    if (!cta || !sticky) return;
    this.#ctaObserver = new IntersectionObserver(([entry]) => {
      sticky.hidden = !mobileQuery.matches || entry.isIntersecting;
    });
    this.#ctaObserver.observe(cta);
  }

  #observeGallery() {
    const gallery = this.querySelector('[data-kh-gallery]');
    const dots = this.querySelectorAll('[data-kh-dots] > span');
    if (!gallery || dots.length === 0) return;
    const shots = [...gallery.children];
    this.#galleryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = shots.indexOf(entry.target);
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        });
      },
      { root: gallery, threshold: 0.6 }
    );
    shots.forEach((shot) => this.#galleryObserver?.observe(shot));
  }
}

if (!customElements.get('kh-product')) customElements.define('kh-product', KhProduct);
